import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Message, useToaster } from "rsuite";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import PlusIcon from "@rsuite/icons/Plus";
import TrashIcon from "@rsuite/icons/Trash";
import {
  SectionRibbon,
  Button,
  FormActions,
  Input,
  Picker,
  GlassCard,
  SpacedGlassCard,
} from "../../../../../ui";

export const GenerateConfigLog = ({ configs, regex_models }) => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  const initConfigs = configs?.length
    ? configs.map((config) => ({
        system_id: config?.system_id || id,
        file_name: config?.file_name || null,
        dir_name: config?.dir_name || null,
        regex_models: config?.regex_models || [],
        pg_tables: config?.pg_tables || [],
      }))
    : [
        {
          system_id: id,
          file_name: null,
          dir_name: null,
          regex_models: [],
          pg_tables: [],
        },
      ];

  const [formConfigs, setFormConfigs] = useState(initConfigs);
  const [loading, setLoading] = useState(false);

  // Update form when configs prop changes (e.g., on page refresh)
  useEffect(() => {
    if (configs?.length) {
      console.log("LOG configs received:", configs);
      const updatedConfigs = configs.map((config) => {
        console.log("Processing LOG config:", config);
        return {
          system_id: config?.system_id || id,
          file_name: config?.file_name ?? null,
          dir_name: config?.dir_name ?? null,
          regex_models: config?.regex_models ?? [],
          pg_tables: config?.pg_tables ?? [],
        };
      });
      console.log("Updated LOG configs:", updatedConfigs);
      setFormConfigs(updatedConfigs);
    }
  }, [configs, id]);

  const pgTableOptions = [
    "siemens_cv",
    "siemens_ct",
    "siemens_mri",
    "ge_cv_syserror",
    "ge_ct_gesys",
    "ge_mri_gesys",
    "philips_ct_events",
    "philips_mri_logcurrent",
    "philips_cv_eventlog",
    "lod",
    "philips_ct_eal",
    "philips_cv_eventlog",
  ].map((item) => ({ label: item, value: item }));

  const regexModelOptions = [
    "cv_bulk",
    "cv_groups",
    "ct_eal_v_2",
    "mri_logcurrent",
    "ct_events_v2",
    "eventlog",
    "re_v1",
    "sys_error",
    "block",
    "sub_block",
  ].map((item) => ({ label: item, value: item }));

  const addConfig = () => {
    setFormConfigs([
      ...formConfigs,
      {
        system_id: id,
        file_name: null,
        dir_name: null,
        regex_models: [],
        pg_tables: [],
      },
    ]);
  };

  const removeConfig = (index) => {
    if (formConfigs.length > 1) {
      setFormConfigs(formConfigs.filter((_, i) => i !== index));
    }
  };

  const updateConfig = (index, field, value) => {
    // Handle both direct values and event objects
    const actualValue = value?.target ? value.target.value : value;
    const updatedConfigs = [...formConfigs];
    updatedConfigs[index] = { ...updatedConfigs[index], [field]: actualValue };
    setFormConfigs(updatedConfigs);
  };

  const resetForm = () => {
    setFormConfigs(initConfigs);
  };

  const submitConfigs = async () => {
    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/update/config`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          configs: formConfigs,
          type: "log",
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          LOG configurations updated successfully.
        </Message>,
        "topCenter",
      );
    } catch (error) {
      console.error("Failed to update LOG configs:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update LOG configurations. Please try again.
        </Message>,
        "topCenter",
      );
    }

    setLoading(false);
  };

  const hasChanges =
    formConfigs.some((config, index) => {
      const initConfig = initConfigs[index];
      if (!initConfig) return true;
      return Object.keys(config).some((key) => config[key] !== initConfig[key]);
    }) || formConfigs.length !== initConfigs.length;

  return (
    <LOGContainer>
      <SectionRibbon
        title="LOG Configuration"
        variant="primary"
        rightContent={
          <Button variant="outline" size="small" onClick={addConfig}>
            <PlusIcon style={{ marginRight: "0.5rem" }} />
            Add Config
          </Button>
        }
      />

      <ConfigurationForm>
        {formConfigs.map((config, index) => (
          <SpacedGlassCard key={index}>
            <FormContent>
              <SectionHeader>
                <SectionTitle>LOG Configuration {index + 1}</SectionTitle>
                {formConfigs.length > 1 && (
                  <RemoveButton
                    variant="subtle"
                    size="small"
                    onClick={() => removeConfig(index)}
                  >
                    <TrashIcon />
                  </RemoveButton>
                )}
              </SectionHeader>

              <FormGrid>
                <FormField span="full">
                  <Input
                    label="System ID"
                    value={config.system_id}
                    onChange={(value) =>
                      updateConfig(index, "system_id", value)
                    }
                    disabled
                    placeholder="System ID"
                  />
                </FormField>

                <FormField>
                  <Input
                    label="File Name"
                    value={config.file_name}
                    onChange={(value) =>
                      updateConfig(index, "file_name", value)
                    }
                    placeholder="Enter file name"
                  />
                </FormField>

                <FormField>
                  <Input
                    label="Directory Name"
                    value={config.dir_name}
                    onChange={(value) => updateConfig(index, "dir_name", value)}
                    placeholder="Enter directory name"
                  />
                </FormField>

                <FormField>
                  <Picker
                    data={pgTableOptions}
                    value={config.pg_tables}
                    onChange={(value) =>
                      updateConfig(index, "pg_tables", value)
                    }
                    placeholder="Select tables"
                    searchable={false}
                    multiple
                  />
                </FormField>

                <FormField>
                  <Picker
                    data={regexModelOptions}
                    value={config.regex_models}
                    onChange={(value) =>
                      updateConfig(index, "regex_models", value)
                    }
                    placeholder="Select regex models"
                    searchable={false}
                    multiple
                  />
                </FormField>
              </FormGrid>
            </FormContent>
          </SpacedGlassCard>
        ))}

        <GlobalActions>
          <Button variant="subtle" onClick={resetForm} disabled={loading}>
            Reset
          </Button>
          <Button variant="primary" onClick={submitConfigs} loading={loading}>
            Submit
          </Button>
        </GlobalActions>
      </ConfigurationForm>

      <ConfigurationInfo>
        <GlassCard>
          <InfoContent>
            <InfoHeader>
              <InfoTitle>LOG Configuration Guidelines</InfoTitle>
            </InfoHeader>

            <InfoGrid>
              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Log File Management</InfoItemTitle>
                  <InfoItemText>
                    Configure log file names and directory paths for system log
                    processing. Ensure log files are accessible and properly
                    formatted.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Pattern Matching</InfoItemTitle>
                  <InfoItemText>
                    Select regex models that match your log format and
                    extraction needs. Different models are optimized for
                    different log types.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Data Storage</InfoItemTitle>
                  <InfoItemText>
                    Configure target database tables and aggregation rules for
                    processed log data. Set up proper column mappings for
                    efficient data storage.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>
            </InfoGrid>
          </InfoContent>
        </GlassCard>
      </ConfigurationInfo>
    </LOGContainer>
  );
};

// Styled Components
const LOGContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ConfigurationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormContent = styled.div`
  padding: 2rem;
`;

const GlobalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
  flex: 1;
`;

const RemoveButton = styled(Button)`
  margin-left: auto;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  align-items: end;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ span }) => {
    if (span === "full") return "grid-column: 1 / -1;";
    if (span === "double") return "grid-column: span 2;";
    return "";
  }}

  @media (max-width: 1200px) {
    ${({ span }) => {
      if (span === "double") return "grid-column: span 2;";
      return "grid-column: span 1;";
    }}
  }

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ConfigurationInfo = styled.div`
  margin-top: 1rem;
`;

const InfoContent = styled.div`
  padding: 2rem;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 4px solid #3498db;
`;

const InfoItemIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoItemContent = styled.div`
  flex: 1;
`;

const InfoItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoItemText = styled.p`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.5;
`;
